import { FC, useMemo, useRef, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import useRoiPractitioner from 'hooks/useRoiPractitioner';
import { COLOR_CODES } from 'utils/constants';

ChartJS.register(ArcElement, Tooltip, Legend);

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  label: string;
  value: number;
  pct: string;
  color: string;
}

interface PreferredPractitionerProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  clinicIds?: string[];
}

const PreferredPractitioner: FC<PreferredPractitionerProps> = ({
  selectedDateRange,
  hourType,
  clinicIds,
}) => {
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    label: '',
    value: 0,
    pct: '',
    color: '',
  });
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isValidating } = useRoiPractitioner({
    startDate: selectedDateRange.fromDate,
    endDate: selectedDateRange.toDate,
    hourType,
    clinicIds,
  });

  const practitioners = useMemo(
    () =>
      (data?.periodData?.practitioners ?? []).filter(
        (p) => p.totalAppointments > 0
      ),
    [data]
  );

  const isEmpty = practitioners.length === 0;

  const chartData = useMemo(
    () => ({
      labels: isEmpty ? ['No data'] : practitioners.map((p) => p.name),
      datasets: [
        {
          data: isEmpty ? [1] : practitioners.map((p) => p.totalAppointments),
          backgroundColor: isEmpty
            ? ['#E8E8E8']
            : practitioners.map((_, i) => COLOR_CODES[i % COLOR_CODES.length]),
          borderColor: '#FFFFFF',
          borderWidth: isEmpty ? 0 : 1,
          hoverBorderColor: '#FFFFFF',
          hoverBorderWidth: isEmpty ? 0 : 1,
          hoverOffset: 0,
        },
      ],
    }),
    [practitioners, isEmpty]
  );

  return (
    <div className="p-2.4 shadow-elevation-06-hover bg-white flex-1 min-h-[38rem] flex flex-col">
      <span className="text-16 font-bold leading-[2.4rem]">
        Preferred Practitioner
      </span>

      {isLoading || isValidating ? (
        <div className="mt-1.6 h-[22rem] flex items-center justify-center">
          <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
        </div>
      ) : (
        <>
          <div ref={chartContainerRef} className="mt-1.6 h-[22rem] relative">
            <Doughnut
              data={chartData}
              options={{
                cutout: '60%',
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    enabled: false,
                    external: isEmpty
                      ? undefined
                      : ({ chart, tooltip: tip }: any) => {
                          if (tip.opacity === 0) {
                            setTooltip((prev) => ({
                              ...prev,
                              visible: false,
                            }));
                            return;
                          }

                          const dataIndex = tip.dataPoints?.[0]?.dataIndex ?? 0;
                          const practitioner = practitioners[dataIndex];
                          if (!practitioner) return;

                          const value = practitioner.totalAppointments;
                          const total = practitioners.reduce(
                            (a, b) => a + b.totalAppointments,
                            0
                          );
                          const pct =
                            total > 0
                              ? ((value / total) * 100).toFixed(1)
                              : '0.0';
                          const label = practitioner.name;
                          const color =
                            COLOR_CODES[dataIndex % COLOR_CODES.length];

                          const containerRect =
                            chartContainerRef.current?.getBoundingClientRect();
                          const canvasRect =
                            chart.canvas.getBoundingClientRect();

                          const x =
                            canvasRect.left -
                            (containerRect?.left ?? 0) +
                            tip.caretX;
                          const y =
                            canvasRect.top -
                            (containerRect?.top ?? 0) +
                            tip.caretY;

                          setTooltip({
                            visible: true,
                            x,
                            y,
                            label,
                            value,
                            pct,
                            color,
                          });
                        },
                  },
                },
              }}
            />

            {tooltip.visible && (
              <div
                className="absolute z-20 pointer-events-none bg-white text-darkest-grey text-14 leading-[2.1rem] px-1.6 py-1.2 rounded-[1rem] shadow-primary whitespace-nowrap"
                style={{
                  left: tooltip.x,
                  top: tooltip.y,
                  transform: 'translate(0, -110%)',
                }}
              >
                <div className="font-bold mb-0.4">{tooltip.label}</div>
                <div className="flex items-center gap-x-0.8">
                  <span
                    className="w-0.8 h-0.8 rounded-full shrink-0"
                    style={{ backgroundColor: tooltip.color }}
                  />
                  <span>{tooltip.value.toLocaleString()} appts</span>
                  <span className="text-mid-grey">({tooltip.pct}%)</span>
                </div>
              </div>
            )}
          </div>

          {!isEmpty && (
            <div className="mt-4 flex flex-wrap justify-center gap-y-1.6 gap-x-2.4 mx-auto">
              {practitioners.map((p, i) => (
                <div key={p.name} className="flex items-center gap-x-0.8">
                  <span
                    className="w-0.8 h-0.8 rounded-full shrink-0"
                    style={{
                      backgroundColor: COLOR_CODES[i % COLOR_CODES.length],
                    }}
                  />
                  <span className="text-14 text-darkest-grey">{p.name}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PreferredPractitioner;
